import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Wrench, Plus } from "lucide-react";

const TYPES = ["FORKLIFT", "SCANNER", "PALLET_JACK", "CONVEYOR", "REACH_TRUCK"];
const STATUSES = ["ACTIVE", "MAINTENANCE", "OFFLINE"];

export default function Equipment() {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ code: "", type: "FORKLIFT", name: "", assigned_to: "", status: "ACTIVE", last_serviced: "" });

  const load = async () => { const { data } = await api.get("/warehouse/equipment"); setList(data); };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try { await api.post("/warehouse/equipment", form); toast.success("Equipment added"); setOpen(false); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const updateStatus = async (id, status) => {
    try { await api.patch(`/warehouse/equipment/${id}`, { status }); toast.success("Status updated"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const tone = (s) => s === "ACTIVE" ? "bg-emerald-600 hover:bg-emerald-700" : s === "MAINTENANCE" ? "bg-amber-500 hover:bg-amber-600" : "bg-red-600 hover:bg-red-700";

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-4 sm:space-y-6" data-testid="warehouse-equipment">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-black flex items-center gap-2">
            <Wrench className="w-7 h-7 text-primary" /> Equipment
          </h1>
          <p className="text-muted-foreground">Forklifts, scanners, pallet jacks — status, assignment & service tracking.</p>
        </div>
        <Button onClick={() => setOpen(true)} data-testid="eq-new"><Plus className="w-4 h-4 mr-1" /> Add equipment</Button>
      </div>

      <div className="surface-card p-4 overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Code</TableHead><TableHead>Type</TableHead><TableHead>Name</TableHead>
            <TableHead>Assigned to</TableHead><TableHead>Last serviced</TableHead>
            <TableHead>Status</TableHead><TableHead className="text-right">Quick actions</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {list.map((e) => (
              <TableRow key={e.id} data-testid={`eq-row-${e.code}`}>
                <TableCell className="font-mono text-xs">{e.code}</TableCell>
                <TableCell><Badge variant="outline">{e.type}</Badge></TableCell>
                <TableCell>{e.name || "—"}</TableCell>
                <TableCell>{e.assigned_to || "—"}</TableCell>
                <TableCell className="font-mono text-xs">{e.last_serviced || "—"}</TableCell>
                <TableCell><Badge className={tone(e.status)}>{e.status}</Badge></TableCell>
                <TableCell className="text-right space-x-1">
                  {STATUSES.filter((s) => s !== e.status).map((s) => (
                    <Button key={s} size="sm" variant="outline" onClick={() => updateStatus(e.id, s)} data-testid={`eq-${e.code}-set-${s}`}>{s}</Button>
                  ))}
                </TableCell>
              </TableRow>
            ))}
            {list.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-10">No equipment yet.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add equipment</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Code</Label><Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} data-testid="eq-code" placeholder="FL-03" /></div>
              <div>
                <Label>Type</Label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="col-span-2"><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
              <div><Label>Assigned to</Label><Input value={form.assigned_to} onChange={(e) => setForm({ ...form, assigned_to: e.target.value })} /></div>
              <div><Label>Last serviced</Label><Input type="date" value={form.last_serviced} onChange={(e) => setForm({ ...form, last_serviced: e.target.value })} /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={!form.code} data-testid="eq-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
